import type { Expression, MascotMove } from '../types/expression'
import { ExpressionCue } from '../gen/service/creativefabrica/studioagent/v1/types_pb'
import type { ExpressionCueJson } from '../gen/service/creativefabrica/studioagent/v1/types_pb'
import type { ChatMessage } from '../types'

const faceByCueName = {
  EXPRESSION_CUE_FOCUSED: 'focused',
  EXPRESSION_CUE_CONFIDENT: 'confident',
  EXPRESSION_CUE_AMUSED: 'amused',
  EXPRESSION_CUE_HEARTBEAT: 'heartbeat',
  EXPRESSION_CUE_REVEAL: 'reveal',
  EXPRESSION_CUE_RELAXED: 'relaxed',
  EXPRESSION_CUE_ATTENTIVE: 'attentive',
  EXPRESSION_CUE_CURIOUS: 'curious',
  EXPRESSION_CUE_THINKING: 'thinking',
  EXPRESSION_CUE_SPEAKING: 'speaking',
  EXPRESSION_CUE_DELIGHTED: 'delighted',
  EXPRESSION_CUE_SURPRISED: 'surprised',
  EXPRESSION_CUE_UNSURE: 'unsure',
  EXPRESSION_CUE_REASSURING: 'reassuring',
} as const satisfies Record<Exclude<ExpressionCueJson, 'EXPRESSION_CUE_UNSPECIFIED'>, Expression>

const cueByNumber: Record<number, ExpressionCueJson> = {
  [ExpressionCue.FOCUSED]: 'EXPRESSION_CUE_FOCUSED',
  [ExpressionCue.CONFIDENT]: 'EXPRESSION_CUE_CONFIDENT',
  [ExpressionCue.AMUSED]: 'EXPRESSION_CUE_AMUSED',
  [ExpressionCue.HEARTBEAT]: 'EXPRESSION_CUE_HEARTBEAT',
  [ExpressionCue.REVEAL]: 'EXPRESSION_CUE_REVEAL',
  [ExpressionCue.RELAXED]: 'EXPRESSION_CUE_RELAXED',
  [ExpressionCue.ATTENTIVE]: 'EXPRESSION_CUE_ATTENTIVE',
  [ExpressionCue.CURIOUS]: 'EXPRESSION_CUE_CURIOUS',
  [ExpressionCue.THINKING]: 'EXPRESSION_CUE_THINKING',
  [ExpressionCue.SPEAKING]: 'EXPRESSION_CUE_SPEAKING',
  [ExpressionCue.DELIGHTED]: 'EXPRESSION_CUE_DELIGHTED',
  [ExpressionCue.SURPRISED]: 'EXPRESSION_CUE_SURPRISED',
  [ExpressionCue.UNSURE]: 'EXPRESSION_CUE_UNSURE',
  [ExpressionCue.REASSURING]: 'EXPRESSION_CUE_REASSURING',
}

const moveByFace: Partial<Record<Expression, MascotMove>> = {
  relaxed: 'little_orbit',
  attentive: 'message_received',
  curious: 'tiptoe_peek',
  delighted: 'happy_landing',
  surprised: 'gentle_recoil',
  reassuring: 'double_nod',
}

export function faceFromCue(cue: ExpressionCue | ExpressionCueJson | undefined): Expression | null {
  if (
    cue === undefined ||
    cue === ExpressionCue.UNSPECIFIED ||
    cue === 'EXPRESSION_CUE_UNSPECIFIED'
  ) {
    return null
  }
  const name = typeof cue === 'number' ? cueByNumber[cue] : cue
  if (!name || name === 'EXPRESSION_CUE_UNSPECIFIED') {
    return null
  }
  return faceByCueName[name]
}

export function expressionCueFromJson(cue: ExpressionCueJson | undefined): ExpressionCue {
  const face = faceFromCue(cue)
  if (!face) {
    return ExpressionCue.UNSPECIFIED
  }
  const match = Object.entries(cueByNumber).find(([, name]) => {
    if (name === 'EXPRESSION_CUE_UNSPECIFIED') {
      return false
    }
    return faceByCueName[name] === face
  })
  return match ? Number(match[0]) : ExpressionCue.UNSPECIFIED
}

export function moveForFace(face: Expression | null): MascotMove | null {
  if (!face) {
    return 'message_received'
  }
  return moveByFace[face] ?? null
}

export function isGenerating(message: ChatMessage | undefined) {
  return Boolean(
    message &&
    message.role === 'assistant' &&
    message.isStreaming &&
    !message.text &&
    message.media?.status === 'generating',
  )
}

export function latestSettledAssistant(messages: readonly ChatMessage[]): ChatMessage | null {
  for (let index = messages.length - 1; index >= 0; index -= 1) {
    const message = messages[index]
    if (
      message &&
      message.role === 'assistant' &&
      !message.isStreaming &&
      !message.isProgressUpdate &&
      message.text
    ) {
      return message
    }
  }
  return null
}
